import {
  useEffect,
  useRef,
  useState
} from 'react'
import { useDispatch } from 'react-redux'
import {
  Image,
  Stack,
  Textarea
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { COLORS } from 'utils/styles'
import { ImagesPng } from 'assets/images'
import {
  deleteComments,
  getNotificationMessage,
  postComments,
  putComments,
  setDeleteCommentsStatus
} from 'store'
import {
  PENDING,
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import { LoadingSpinier } from 'common/Loading'
import {
  BtnPill,
  DeletePopupBtn,
  DeletePopupText
} from './styled'

const MotionStack = motion(Stack)

export const CreateComment = ({
  activeProfile,
  blogParent,
  getCommentsStatus,
  type,
  postCommentsStatus,
  putCommentsStatus,
  editCommentData,
  setEditCommentData,
  deleteCommentsStatus
}) => {
  const dispatch = useDispatch()
  const formBodyRef = useRef(null)
  const parentStackRef = useRef(null)
  const [body, setBody] = useState('')
  const [show, setShow] = useState(false)
  const [openDeleteComment, setOpenDeleteComment] = useState(false)

  const closeForm = () => {
    setBody('')
    setEditCommentData({})
    setOpenDeleteComment(false)
  }

  const onGoingApiCall = putCommentsStatus === PENDING
    || postCommentsStatus === PENDING
    || deleteCommentsStatus === PENDING
    || getCommentsStatus === PENDING

  const submitForm = () => {
    if (body.trim() === '') {
      dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'Body field is required',
          isEditProfile: true
        })
      )
    } else if (Object.keys(editCommentData).length === 0) {
      dispatch(postComments({
        id: activeProfile.id,
        type,
        ...(
          type === 'post'
            ? { post_id: blogParent[blogParent.length - 1].id }
            : { comment_id: blogParent[blogParent.length - 1].comment_id }
        ),
        body
      }))
    } else {
      dispatch(putComments({
        id: activeProfile.id,
        type,
        comment_id: editCommentData.comment_id,
        body
      }))
    }
  }

  const deleteComment = async () => {
    await dispatch(deleteComments({
      id: editCommentData.profile_id,
      comment_id: editCommentData.comment_id
    }))

    setTimeout(() => dispatch(setDeleteCommentsStatus(0)), 1000)

    closeForm()
  }

  const focusBodyRef = () => {
    if (formBodyRef.current) {
      formBodyRef.current.blur()
      formBodyRef.current.focus()
    }
  }

  const handleResize = () => {
    if (formBodyRef.current) {
      formBodyRef.current.style.height = '5px'
      formBodyRef.current.style.height = `
        ${formBodyRef.current.scrollHeight}px
      `
    }
  }

  useEffect(() => {
    handleResize()
  }, [body])

  useEffect(() => {
    if (Object.keys(editCommentData).length !== 0) {
      setBody(editCommentData.body)
      focusBodyRef()
    }
  }, [editCommentData])

  useEffect(() => {
    if (postCommentsStatus === SUCCEEDED) {
      closeForm()
    }
  }, [postCommentsStatus])

  useEffect(() => {
    if (putCommentsStatus === SUCCEEDED) {
      closeForm()
    }
  }, [putCommentsStatus])

  useEffect(() => {
    closeForm()
  }, [blogParent])

  useEffect(() => {
    focusBodyRef()

    setTimeout(() => setShow(true), 850)
  }, [])

  return show
    ? (
      <MotionStack
        initial={{ height: 0 }}
        animate={{ height: 'auto' }}
        transition={{
          type: 'tween',
          duration: 0.8
        }}
        ref={parentStackRef}
        transformOrigin="top"
        overflow="hidden"
        background={COLORS.grayGradientTopToBot}
        width="100%"
        border="solid 1px black"
        borderRadius="3px"
        flexShrink={0}
        position="relative"
      >
        <Stack
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          flexDirection="row"
          padding={body.length ? '8px 8px 0 8px' : '8px 8px 12px 8px'}
          transition="0.5s ease"
        >
          <Image
            boxSize="45px"
            objectFit="cover"
            src={activeProfile.profile_picture || ImagesPng.DefaultImage}
            alt="User Image"
            borderRadius="full"
            display="inline-flex"
          />

          <Textarea
            ref={formBodyRef}
            background={COLORS.lighterGray}
            placeholder="Add your comment here"
            width="100%"
            height="43px"
            minHeight="2.8em"
            overflow="hidden"
            resize="none"
            name="blog-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </Stack>
        <Stack
          opacity={openDeleteComment ? 1 : 0}
          transition="1s"
          position="absolute"
          backgroundColor="rgb(228, 151, 144)"
          padding="15px"
          transform={openDeleteComment ? 'translateY(-50%)' : 'none'}
          top={openDeleteComment ? '50%' : '0px'}
          right="50px"
          left="50px"
          borderRadius="5px"
          boxShadow="lg"
          zIndex={openDeleteComment ? 101 : -100}
          flexDir="row"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          sx={{
            '@media (max-height: 550px)': {
              top: '65% !important'
            }
          }}
        >
          <DeletePopupText>Delete this comment?</DeletePopupText>

          <DeletePopupBtn
            id="confirm-delete-comment-btn"
            onClick={() => deleteComment()}
            backgroundColor={(onGoingApiCall ? COLORS.gray_op8 : COLORS.gold) + ' !important'}
            pointerEvents={onGoingApiCall ? 'none' : 'auto'}
            cursor={onGoingApiCall ? 'default' : 'pointer'}
          >
            {
              onGoingApiCall
                ? (
                  <LoadingSpinier
                    width="20px"
                    height="20px"
                    marginTop="5px"
                  />
                ) : 'Delete'
            }
          </DeletePopupBtn>

          <DeletePopupBtn
            id="cancel-delete-comment-btn"
            onClick={() => setOpenDeleteComment(false)}
            backgroundColor={(onGoingApiCall ? COLORS.gray_op8 : COLORS.gold) + ' !important'}
            p="10px"
            pointerEvents={onGoingApiCall ? 'none' : 'auto'}
            cursor={onGoingApiCall ? 'default' : 'pointer'}
          >
            Cancel
          </DeletePopupBtn>
        </Stack>
        <Stack
          direction="row"
          justifyContent="flex-end"
          margin="-10.5px 0 0 0"
          fontSize="13px"
          overflow="hidden"
          display="flex"
          padding={body.length ? '8px' : '0'}
          height={body.length ? '47px' : '0'}
          transition="0.5s ease"
        >
          <BtnPill
            shouldBeDisabled={onGoingApiCall || openDeleteComment}
            data-name="blog-comment-delete"
            onClick={() => setOpenDeleteComment(true)}
            display={Object.keys(editCommentData).length ? 'flex' : 'none'}
          >
            Delete
          </BtnPill>
          <BtnPill
            shouldBeDisabled={onGoingApiCall || openDeleteComment}
            data-name="blog-comment-cancel"
            onClick={() => closeForm()}
          >
            Cancel
          </BtnPill>
          <BtnPill
            shouldBeDisabled={onGoingApiCall || openDeleteComment}
            data-name="blog-comment-post"
            onClick={() => submitForm()}
          >
            {
              putCommentsStatus === PENDING
              || postCommentsStatus === PENDING
                ? (
                  <Stack>
                    <LoadingSpinier
                      width="10px"
                      height="10px"
                      marginTop="23px"
                    />
                  </Stack>
                ) : 'Post'
            }
          </BtnPill>
        </Stack>
      </MotionStack>
    ) : ''
}
